import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  Button,
  List,
  ListItemButton,
  ListItemText,
  TextField,
  Checkbox,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  InputAdornment,
  Divider,
  Snackbar,
} from '@mui/material';
import {
  Security,
  CheckCircle,
  Delete,
  Search,
  AdminPanelSettings,
  Save as SaveIcon,
} from "@mui/icons-material";
import {
  fetchRoles,
  fetchRole,
  createRole,
  updateRole,
  deleteRole,
  fetchPermissionCatalog,
} from '../../services/rbac';

const DEFAULT_FORM = {
  name: '',
  description: '',
};

const RolesPanel = ({
  roleSearchTerm,
  setRoleSearchTerm,
  saving,
  loadingRoles,
  loadingRoleDetail,
  handleNewRole,
  filteredRoles,
  selectedRole,
  handleSelectRole,
}) => {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <AdminPanelSettings color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Roles
          </Typography>
        </Stack>

        <TextField
          value={roleSearchTerm}
          onChange={(event) => setRoleSearchTerm(event.target.value)}
          placeholder="Search roles"
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Button
          variant="contained"
          onClick={handleNewRole}
          disabled={saving || loadingRoles || loadingRoleDetail}
          fullWidth
          sx={{ mb: 2 }}
        >
          New Role
        </Button>

        {loadingRoles ? (
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={28} />
          </Box>
        ) : (
          <List dense sx={{ maxHeight: 520, overflowY: "auto" }}>
            {filteredRoles.map((role) => (
              <ListItemButton
                key={role.id || role.name}
                selected={selectedRole?.id === role.id}
                onClick={() => handleSelectRole(role)}
                sx={{ borderRadius: 1, mb: 0.5 }}
              >
                <ListItemText
                  primary={role.name}
                  secondary={role.is_system ? "System role" : role.description}
                />
              </ListItemButton>
            ))}

            {!filteredRoles.length && (
              <Typography variant="body2" color="text.secondary" align="center">
                No roles found
              </Typography>
            )}
          </List>
        )}
      </CardContent>
    </Card>
  );
};

const RoleDetailPanel = ({
  isSystemRole,
  formData,
  setFormData,
  loadingRoleDetail,
  handleSave,
  saving,
  isEditing,
  setDeleteDialog,
  selectedPermissionIds,
  feedback,
  setFeedback,
}) => {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack spacing={2}>
          <Stack direction="row" justifyContent="space-between" alignItems="center">
            <Typography variant="subtitle1" fontWeight={600}>
              Selected Role
            </Typography>
            {isSystemRole && <Chip label="System role" size="small" color="warning" />}
          </Stack>

          <TextField
            label="Role Name"
            value={formData.name}
            onChange={(event) =>
              setFormData((prev) => ({ ...prev, name: event.target.value }))
            }
            fullWidth
            disabled={isSystemRole || loadingRoleDetail}
          />

          <TextField
            label="Description"
            value={formData.description}
            onChange={(event) =>
              setFormData((prev) => ({
                ...prev,
                description: event.target.value,
              }))
            }
            fullWidth
            disabled={loadingRoleDetail}
          />

          <Grid container spacing={2}>
            <Grid size={12}>
              <Button
                variant="contained"
                onClick={handleSave}
                disabled={saving || loadingRoleDetail}
                fullWidth
              >
                {isEditing ? "Update Role" : "Create Role"}
              </Button>
            </Grid>

            <Grid size={12}>
              <Tooltip title={isSystemRole ? "System roles cannot be deleted" : ""}>
                <span style={{ width: "100%", display: "block" }}>
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<Delete />}
                    onClick={() => setDeleteDialog(true)}
                    disabled={!isEditing || isSystemRole || saving}
                    fullWidth
                  >
                    Delete
                  </Button>
                </span>
              </Tooltip>
            </Grid>
          </Grid>

          <Divider />

          <Typography variant="body2" color="text.secondary">
            Assigned permissions: {selectedPermissionIds.length}
          </Typography>

          <Collapse in={Boolean(feedback)}>
            {feedback && (
              <Alert severity={feedback.type} onClose={() => setFeedback(null)}>
                {feedback.message}
              </Alert>
            )}
          </Collapse>
        </Stack>
      </CardContent>
    </Card>
  );
};

const PermissionCatalogPanel = ({
  loadingCatalog,
  permissionSearchTerm,
  setPermissionSearchTerm,
  filteredCatalog,
  assignedPermissionIds,
  handlePermissionToggle,
}) => {
  if (loadingCatalog) {
    return (
      <Card sx={{ height: "100%" }}>
        <CardContent>
          <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
            <CircularProgress size={32} />
          </Box>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <Security color="primary" />
          <Typography variant="subtitle1" fontWeight={600}>
            Permission Catalog
          </Typography>
        </Stack>

        <TextField
          value={permissionSearchTerm}
          onChange={(event) => setPermissionSearchTerm(event.target.value)}
          placeholder="Search permissions"
          fullWidth
          size="small"
          sx={{ mb: 2 }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <Search fontSize="small" />
              </InputAdornment>
            ),
          }}
        />

        <Stack spacing={2} sx={{ maxHeight: 520, overflowY: "auto" }}>
          {filteredCatalog.map(({ category, permissions }) => (
            <Box key={category?.id || category?.code}>
              <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1 }}>
                {category?.name || "Uncategorized"}
              </Typography>
              <Grid container spacing={1}>
                {permissions.map((permission) => {
                  const isSelected = assignedPermissionIds.has(permission.id);
                  return (
                    <Grid size={{ xs: 12, sm: 6, md: 4 }} key={permission.id}>
                      <Box
                        sx={{
                          border: "1px solid",
                          borderColor: isSelected ? "primary.main" : "divider",
                          borderRadius: 1,
                          p: 1.5,
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Checkbox
                          checked={isSelected}
                          onChange={() => handlePermissionToggle(permission.id)}
                        />
                        <Box>
                          <Typography variant="body2" fontWeight={600}>
                            {permission.name}
                          </Typography>
                          <Typography variant="caption" color="text.secondary">
                            {permission.identifier}
                          </Typography>
                        </Box>
                      </Box>
                    </Grid>
                  );
                })}
              </Grid>
            </Box>
          ))}

          {!filteredCatalog.length && (
            <Typography variant="body2" color="text.secondary" align="center">
              No permissions available
            </Typography>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
};

const EffectivePermissionsPanel = ({ effectivePermissions }) => {
  return (
    <Card sx={{ height: "100%" }}>
      <CardContent>
        <Grid container alignItems="center" spacing={1} sx={{ mb: 2 }}>
          <Grid>
            <CheckCircle color="success" />
          </Grid>
          <Grid>
            <Typography variant="subtitle1" fontWeight={600}>
              Effective Permissions
            </Typography>
          </Grid>
        </Grid>

        <Grid container sx={{ mb: 2 }}>
          <Grid size={12}>
            <Typography variant="body2" color="text.secondary">
              Permissions assigned to this role.
            </Typography>
          </Grid>
        </Grid>

        <Grid
          container
          spacing={1}
          sx={{
            maxHeight: 520,
            overflowY: "auto",
            pb: 2,
          }}
        >
          {effectivePermissions.length > 0 ? (
            effectivePermissions.map((perm) => (
              <Grid key={perm.id} size={{ xs: 12, sm: 6, md: 3 }}>
                <Chip
                  label={perm.name}
                  size="small"
                  color="success"
                  sx={{ width: "100%" }}
                />
              </Grid>
            ))
          ) : (
            <Grid size={12}>
              <Typography variant="body2" color="text.secondary">
                No permissions assigned
              </Typography>
            </Grid>
          )}
        </Grid>
      </CardContent>
    </Card>
  );
};

const RoleManagement = () => {
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]);
  const [filteredRoles, setFilteredRoles] = useState([]);
  const [roleSearchTerm, setRoleSearchTerm] = useState("");
  const [permissionSearchTerm, setPermissionSearchTerm] = useState("");
  const [loadingRoles, setLoadingRoles] = useState(false);
  const [loadingCatalog, setLoadingCatalog] = useState(false);
  const [loadingRoleDetail, setLoadingRoleDetail] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);

  const [selectedRole, setSelectedRole] = useState(null);
  const [formData, setFormData] = useState(DEFAULT_FORM);
  const [selectedPermissionIds, setSelectedPermissionIds] = useState([]);

  const isEditing = Boolean(selectedRole && selectedRole.id);
  const isSystemRole = Boolean(selectedRole?.is_system);

  useEffect(() => {
    if (feedback) setSnackbarOpen(true);
  }, [feedback]);

  useEffect(() => {
    const loadInitialData = async () => {
      setFeedback(null);
      setLoadingCatalog(true);
      setLoadingRoles(true);
      try {
        const [catalogResponse, rolesResponse] = await Promise.all([
          fetchPermissionCatalog(),
          fetchRoles({ page_size: 200 }),
        ]);

        const rolePayload = rolesResponse?.results || rolesResponse || [];
        setCatalog(catalogResponse?.categories || []);
        setRoles(rolePayload);
        setFilteredRoles(rolePayload);

        if (rolePayload.length > 0) {
          handleSelectRole(rolePayload[0]);
        }
      } catch (error) {
        setFeedback({
          type: "error",
          message: error.message || "Failed to load role management data.",
        });
      } finally {
        setLoadingCatalog(false);
        setLoadingRoles(false);
      }
    };

    loadInitialData();
  }, []);

  useEffect(() => {
    if (!roleSearchTerm) {
      setFilteredRoles(roles);
      return;
    }

    const term = roleSearchTerm.toLowerCase();
    setFilteredRoles(
      roles.filter((role) =>
        [role?.name, role?.description]
          .filter(Boolean)
          .some((value) => String(value).toLowerCase().includes(term))
      )
    );
  }, [roleSearchTerm, roles]);

  const handleSelectRole = async (role) => {
    if (!role) {
      setSelectedRole(null);
      setFormData(DEFAULT_FORM);
      setSelectedPermissionIds([]);
      return;
    }

    setSelectedRole(role);
    setFormData({ name: role.name || '', description: role.description || '' });
    setLoadingRoleDetail(true);
    setFeedback(null);
    try {
      const detail = await fetchRole(role.id);
      const permissions = detail?.permissions || [];
      setSelectedPermissionIds(Array.isArray(permissions) ? permissions : []);
    } catch (error) {
      setFeedback({
        type: "error",
        message: error.message || "Failed to load role details.",
      });
      setSelectedPermissionIds([]);
    } finally {
      setLoadingRoleDetail(false);
    }
  };

  const handleNewRole = () => {
    setSelectedRole({ id: null, name: '', description: '', is_system: false });
    setFormData(DEFAULT_FORM);
    setSelectedPermissionIds([]);
    setFeedback(null);
  };

  const handlePermissionToggle = (permissionId) => {
    setSelectedPermissionIds((prev) =>
      prev.includes(permissionId)
        ? prev.filter((id) => id !== permissionId)
        : [...prev, permissionId]
    );
  };

  const assignedPermissionIds = useMemo(() => new Set(selectedPermissionIds || []), [selectedPermissionIds]);
  const filteredCatalog = useMemo(() => {
    if (!permissionSearchTerm) return catalog;
    const term = permissionSearchTerm.toLowerCase();
    return (catalog || [])
      .map((group) => {
        const permissions = Array.isArray(group?.permissions) ? group.permissions : [];
        const nextPermissions = permissions.filter((permission) => {
          const searchable = [
            permission?.name,
            permission?.identifier,
            permission?.path,
            permission?.http_method,
            permission?.description,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          return searchable.includes(term);
        });
        if (!nextPermissions.length) return null;
        return { ...group, permissions: nextPermissions };
      })
      .filter(Boolean);
  }, [catalog, permissionSearchTerm]);

  const permissionLookup = useMemo(() => {
    const map = new Map();
    (catalog || []).forEach((group) => {
      const permissions = Array.isArray(group?.permissions) ? group.permissions : [];
      permissions.forEach((permission) => {
        if (!permission?.id) return;
        map.set(permission.id, permission);
      });
    });
    return map;
  }, [catalog]);

  const effectivePermissions = useMemo(() => {
    return (selectedPermissionIds || [])
      .map((permissionId) => permissionLookup.get(permissionId))
      .filter(Boolean)
      .sort((a, b) => String(a?.name || "").localeCompare(String(b?.name || "")));
  }, [permissionLookup, selectedPermissionIds]);

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setFeedback({ type: "warning", message: "Role name is required." });
      return;
    }

    const payload = {
      name: formData.name.trim(),
      description: formData.description,
      permissions: selectedPermissionIds,
    };

    setSaving(true);
    setFeedback(null);
    try {
      let targetRoleId = isEditing ? selectedRole.id : null;
      if (isEditing) {
        await updateRole(selectedRole.id, payload);
        setFeedback({ type: "success", message: "Role updated successfully." });
      } else {
        const created = await createRole(payload);
        setFeedback({ type: "success", message: "Role created successfully." });
        if (created?.id) targetRoleId = created.id;
      }
      setLoadingRoles(true);
      const response = await fetchRoles({ page_size: 200 });
      const payloadRoles = response?.results || response || [];
      setRoles(payloadRoles);
      setFilteredRoles(payloadRoles);

      const nextSelected =
        (targetRoleId && payloadRoles.find((role) => role.id === targetRoleId)) ||
        (payloadRoles.length ? payloadRoles[0] : null);
      if (nextSelected) await handleSelectRole(nextSelected);
    } catch (error) {
      setFeedback({ type: "error", message: error.message || "Failed to save role." });
    } finally {
      setSaving(false);
      setLoadingRoles(false);
    }
  };

  const handleDelete = async () => {
    if (!selectedRole?.id) return;
    setSaving(true);
    setFeedback(null);
    try {
      await deleteRole(selectedRole.id);
      setFeedback({ type: "success", message: "Role deleted successfully." });
      setDeleteDialog(false);
      setSelectedRole(null);
      setLoadingRoles(true);
      const response = await fetchRoles({ page_size: 200 });
      const payloadRoles = response?.results || response || [];
      setRoles(payloadRoles);
      setFilteredRoles(payloadRoles);
      if (payloadRoles.length) {
        await handleSelectRole(payloadRoles[0]);
      } else {
        handleNewRole();
      }
    } catch (error) {
      setFeedback({ type: "error", message: error.message || "Failed to delete role." });
    } finally {
      setSaving(false);
      setLoadingRoles(false);
    }
  };

  return (
    <Box sx={{ px: { xs: 1, md: 2 }, py: 2 }}>
      <Typography variant="h5" fontWeight={700} sx={{ mb: 2 }}>
        Role Management
      </Typography>

      <Grid container spacing={2}>
        <Grid size={{ xs: 12, md: 2 }}>
          <RolesPanel
            roleSearchTerm={roleSearchTerm}
            setRoleSearchTerm={setRoleSearchTerm}
            saving={saving}
            loadingRoles={loadingRoles}
            loadingRoleDetail={loadingRoleDetail}
            handleNewRole={handleNewRole}
            filteredRoles={filteredRoles}
            selectedRole={selectedRole}
            handleSelectRole={handleSelectRole}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 2 }}>
          <RoleDetailPanel
            isSystemRole={isSystemRole}
            formData={formData}
            setFormData={setFormData}
            loadingRoleDetail={loadingRoleDetail}
            handleSave={handleSave}
            saving={saving}
            isEditing={isEditing}
            setDeleteDialog={setDeleteDialog}
            selectedPermissionIds={selectedPermissionIds}
            feedback={feedback}
            setFeedback={setFeedback}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 8 }}>
          <PermissionCatalogPanel
            loadingCatalog={loadingCatalog}
            permissionSearchTerm={permissionSearchTerm}
            setPermissionSearchTerm={setPermissionSearchTerm}
            filteredCatalog={filteredCatalog}
            assignedPermissionIds={assignedPermissionIds}
            handlePermissionToggle={handlePermissionToggle}
          />
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          <Card>
            <CardContent>
              <Grid container alignItems="center" spacing={2}>
                <Grid>
                  <Button
                    variant="contained"
                    startIcon={<SaveIcon />}
                    onClick={handleSave}
                    disabled={saving || loadingRoleDetail}
                  >
                    {saving ? "Saving..." : "Save Permissions"}
                  </Button>
                </Grid>

                {saving && (
                  <Grid>
                    <CircularProgress size={20} />
                  </Grid>
                )}
              </Grid>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, md: 12 }}>
          <EffectivePermissionsPanel effectivePermissions={effectivePermissions} />
        </Grid>
      </Grid>

      <Dialog open={deleteDialog} onClose={() => setDeleteDialog(false)}>
        <DialogTitle>Delete Role</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the role "{selectedRole?.name}"?
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteDialog(false)} disabled={saving}>Cancel</Button>
          <Button onClick={handleDelete} color="error" disabled={saving}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbarOpen && Boolean(feedback)}
        autoHideDuration={4000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        {feedback ? (
          <Alert
            severity={feedback.type}
            onClose={() => {
              setSnackbarOpen(false);
              setFeedback(null);
            }}
            variant="filled"
            sx={{ width: "100%" }}
          >
            {feedback.message}
          </Alert>
        ) : (
          <span />
        )}
      </Snackbar>
    </Box>
  );
};

export default RoleManagement;
